import { ASSET_TYPE, ViewerEventType } from '@showpad/asset-viewer-v2';
import { IframePresentationRenderer } from '@showpad/asset-viewer-v2/release/esm/components/renderers/IframePresentationRenderer';
import AppFocusHelper from '../../helpers/app-focus.helper';
import { isMobile } from '../../helpers/detect-agent-browser';
import { logStart, logStop, logUpdate } from '../../helpers/device-event-logger.helper';
import { hasDeviceEventData } from '../../helpers/device-event.helper';
import Dom from '../../helpers/dom-helper';
import { UPDATE_ASSET_THRESHOLD, UPDATE_THRESHOLD } from '../../helpers/event-logger';
import { isPageHidden, isPageVisibilitySupported, pageVisibilityChangedListener } from '../../helpers/page-visibility';

const INDEX_NOT_FOUND = -1;
const CAN_LOG_UNFOCUSED = [ASSET_TYPE.AUDIO, ASSET_TYPE.VIDEO];
const LOG_CREATED_IN_EVENT = [ASSET_TYPE.AUDIO, ASSET_TYPE.VIDEO, ASSET_TYPE.URL, ASSET_TYPE['3D-MODEL'], ASSET_TYPE.MEETING_VIDEO];
const BRIEF_PAUSE_THRESHOLD = 2000; // 2 seconds in milliseconds

export default class ShowpadAssetViewerLogger {
    // # DeviceEvent logging:
    //
    // - IMAGE      starts logging after being viewed UPDATE_THRESHOLD time in the viewport and is updated every UPDATE_ASSET_THRESHOLD
    // - DOCUMENT   starts logging after being viewed UPDATE_THRESHOLD time in the viewport and is updated every UPDATE_ASSET_THRESHOLD
    // - VIDEO      starts logging as it's being played, continues on brief pause (< 2s), stops logging when end or long pause (>= 2s), not required to be in the viewport, continues logging when app loses focus or scrolled away
    // - AUDIO      starts logging as it's being played, continues on brief pause (< 2s), stops logging when end or long pause (>= 2s), not required to be in the viewport, continues logging when app loses focus or scrolled away
    // - WEBAPP     starts logging as it's fullscreen, stops logging when webapp close (in 'feed' a preview is shown), unless when there's only 1 assetViewerItem, then the web-app is rendered inline and needs to follow the regular flow // SP-41148
    // - URL        log once after url has been clicked
    // - 3D-MODEL   starts logging when model is loaded after button click

    constructor(eventLogger, assetViewer, eventBus) {
        this.eventLogger = eventLogger;
        this.assetViewer = assetViewer;
        this.eventBus = eventBus;
        this.childAssetViewer = null;

        // there are multiple events that can trigger a blur or resume, prevent multiple executions
        this.isLogging = false;
        // don't refocus on event if a modal or sidebar is open
        this.canRefocus = true;

        this.lastFocusedParentAssetViewerItem = null;
        this.lastLoggedEvent = {};
        this.lastLoggedEventMap = {};
        this.parentEventPromiseMap = {};

        // Track pause times for CAN_LOG_UNFOCUSED assets to determine if we should resume or create new event
        this.lastPauseTimeMap = {};

        this.appFocusHelper = new AppFocusHelper();
        this.viewportAssetItem = null;
        this.firstRun = true;

        this.parentPageDeviceEvent = null;

        // Track which asset IDs are currently being logged from the overlay
        this.overlayEventIds = new Set();
    }
    /**
     * Attach eventlisteners to the associated events for logging deviceevents
     */
    startLogging() {
        this.isLogging = true;

        this.listenToFocusChange();

        // Track navigation events sent by showpadjs
        Dom.on(window, 'message', (message) => {
            if (message.data && message.data.type === 'showpad-track-event' && message.data.data && message.data.data.event) {
                const events = Array.isArray(message.data.data.event) ? message.data.data.event : [message.data.data.event];
                const assetViewer = this.childAssetViewer || this.assetViewer;
                const currentView = assetViewer.viewer.currentView;

                if (!currentView || (currentView && !currentView.assetViewerItem)) {
                    throw new Error('showpad-track-event received without parent assetViewerItem');
                }

                const activeAssetViewerItemId = currentView.assetViewerItem.id;
                const activeAssetViewerItemPosition =
                    typeof currentView.assetViewerItem.position === 'number'
                        ? currentView.assetViewerItem.position
                        : currentView.assetViewerItem.parentPosition;
                const parentEventPromise = this.parentEventPromiseMap[activeAssetViewerItemId];

                if (parentEventPromise) {
                    parentEventPromise
                        .then((parentEvent) => {
                            if (currentView.assetViewerItem.type === ASSET_TYPE.WEBAPP) {
                                this.eventLogger.trackHtmlNavigation(events, {
                                    parentId: parentEvent.id,
                                    assetPosition: activeAssetViewerItemPosition,
                                    assetId: this.childAssetViewer ? currentView.assetViewerItem.assetId : undefined,
                                    displayName: currentView.assetViewerItem.displayName,
                                });
                            }
                        })
                        .catch(() => {});
                }
            }
        });

        this.addAssetViewerEventListeners(this.assetViewer);

        // on desktop, out of focus means we stop logging
        // mobile browsers don't always get to trigger blur, fallback on page visibility
        if (isMobile()) {
            const withPageVisibilityApi = true;

            this.fallbackBackgroundEventLogging(withPageVisibilityApi);
        }
    }

    registerChildAssetViewer(assetViewer) {
        this.childAssetViewer = assetViewer;
        const unregisterAssetViewerEventListeners = this.addAssetViewerEventListeners(assetViewer);
        return function unregisterChildAssetViewer() {
            unregisterAssetViewerEventListeners();
            this.childAssetViewer = null;
        };
    }

    addAssetViewerEventListeners(assetViewer) {
        const onPublicRenderedViewFocus = (event) => {
            if (this.appFocusHelper.isAppFocused) {
                const view = assetViewer.getAssetViewerViewComponentById(event.payload.assetViewerView.id);
                const page = view.assetViewerItem.type === ASSET_TYPE.DOCUMENT ? view.page : null;

                // prettier-ignore
                if (
                    // If there is no previous item
                    this.lastFocusedParentAssetViewerItem === null ||

                    // or if the last focused item is different than what we have on the viewport
                    (this.lastFocusedParentAssetViewerItem !== null && this.lastFocusedParentAssetViewerItem.id !== view.assetViewerItem.id) ||

                    // or it's the same item but with a different page (document type)
                    (this.lastFocusedParentAssetViewerItem !== null &&
                        this.lastFocusedParentAssetViewerItem.id === view.assetViewerItem.id &&
                        this.lastFocusedParentAssetViewerItem.page !== page)
                ) {

                    if (typeof view.assetViewerItem.position === 'number') {
                        this.lastFocusedParentAssetViewerItem = view.assetViewerItem;
                    }

                    if (view.assetViewerItem.type === ASSET_TYPE.URL && view.presentationRenderer instanceof IframePresentationRenderer) {
                        this.eventLogger.createUrlAssetViewDeviceEvent(view.assetViewerItem).catch(() => {});
                    } else {
                        this.handleViewEvents(view.assetViewerItem, page, true);
                    }
                }
            }
        };

        const onPlaybackStart = (event) => {
            if (this.eventLogger.showcaseViewedEventSent) {
                this.handleDynamicViewEvent(event.payload.assetViewerView.assetViewerItem);
            } else {
                // For videos, if the user presses play before 'showcase-view' event is sent (before tracking starts),
                // we need to send that event before sending any 'asset-view' events
                this.eventLogger
                    .trackShowcaseView()
                    .then(() => this.handleDynamicViewEvent(event.payload.assetViewerView.assetViewerItem))
                    .catch(() => {});
            }
        };

        const onPlaybackPause = (event) => {
            const assetViewerItem = event.payload.assetViewerView.assetViewerItem;

            // Record the pause time for this asset to determine if we should resume or create new event on play
            this.lastPauseTimeMap[assetViewerItem.id] = new Date().getTime();

            // Stop the heartbeat updates but keep the event data
            if (hasDeviceEventData(this.lastLoggedEventMap[assetViewerItem.id])) {
                const loggedEventStore = this.lastLoggedEventMap[assetViewerItem.id];

                if (loggedEventStore.updateInterval) {
                    clearInterval(loggedEventStore.updateInterval);
                    loggedEventStore.updateInterval = null;
                }

                // Set a timeout to stop the event if pause lasts more than 2 seconds
                if (loggedEventStore.pauseTimeout) {
                    clearTimeout(loggedEventStore.pauseTimeout);
                }

                loggedEventStore.pauseTimeout = setTimeout(() => {
                    this.resetDynamicDeviceEvent(assetViewerItem);
                    delete this.lastPauseTimeMap[assetViewerItem.id];
                }, BRIEF_PAUSE_THRESHOLD);
            }
        };

        const onPlaybackEnd = (event) => {
            this.resetDynamicDeviceEvent(event.payload.assetViewerView.assetViewerItem);
        };

        const onWebappOpen = (event) => {
            this.handleDynamicViewEvent(event.payload.assetViewerItem);
        };

        const onWebappClose = (event) => {
            this.resetDynamicDeviceEvent(event.payload.assetViewerItem);
        };

        const onStartViewing3dModel = (event) => {
            this.handleDynamicViewEvent(event.payload.assetViewerView.assetViewerItem);
        };

        const onStopViewing3dModel = (event) => {
            this.handleViewEvents(event.payload.assetViewerView.assetViewerItem);
        };

        const onUrlAssetClicked = (event) => {
            this.eventLogger.createUrlAssetViewDeviceEvent(event.payload.assetViewerView.assetViewerItem).catch(() => {});
        };

        const onDownloadAsset = (event) => {
            this.eventLogger.trackItemDownload(event.payload.assetViewerItem);
        };

        const onFocusChange = (event) => {
            this.viewportAssetItem = { item: event.payload.assetViewerView.assetViewerItem, page: event.payload.assetViewerView.page };
        };

        // handle device from asset-viewer for view and focus events
        assetViewer.eventBus.on(ViewerEventType.PublicRenderedViewFocus, onPublicRenderedViewFocus);
        // handle device from asset-viewer for "dynamic" assets being viewed that need extra interaction
        assetViewer.eventBus.on(ViewerEventType.PlaybackStart, onPlaybackStart);
        assetViewer.eventBus.on(ViewerEventType.PlaybackPause, onPlaybackPause);
        assetViewer.eventBus.on(ViewerEventType.PlaybackEnd, onPlaybackEnd);
        assetViewer.eventBus.on(ViewerEventType.WebappOpen, onWebappOpen);
        assetViewer.eventBus.on(ViewerEventType.WebappClose, onWebappClose);
        assetViewer.eventBus.on(ViewerEventType.StartViewing3dModel, onStartViewing3dModel);
        assetViewer.eventBus.on(ViewerEventType.StopViewing3dModel, onStopViewing3dModel);
        assetViewer.eventBus.on(ViewerEventType.UrlAssetClicked, onUrlAssetClicked);
        // handle download event from asset-viewer
        assetViewer.eventBus.on(ViewerEventType.DownloadAsset, onDownloadAsset);
        assetViewer.eventBus.on(ViewerEventType.FocusChange, onFocusChange);

        return function removeAssetViewerEventListeners() {
            assetViewer.eventBus.off(ViewerEventType.PublicRenderedViewFocus, onPublicRenderedViewFocus);
            assetViewer.eventBus.off(ViewerEventType.PlaybackStart, onPlaybackStart);
            assetViewer.eventBus.off(ViewerEventType.PlaybackPause, onPlaybackPause);
            assetViewer.eventBus.off(ViewerEventType.PlaybackEnd, onPlaybackEnd);
            assetViewer.eventBus.off(ViewerEventType.WebappOpen, onWebappOpen);
            assetViewer.eventBus.off(ViewerEventType.WebappClose, onWebappClose);
            assetViewer.eventBus.off(ViewerEventType.StartViewing3dModel, onStartViewing3dModel);
            assetViewer.eventBus.off(ViewerEventType.StopViewing3dModel, onStopViewing3dModel);
            assetViewer.eventBus.off(ViewerEventType.UrlAssetClicked, onUrlAssetClicked);
            assetViewer.eventBus.off(ViewerEventType.DownloadAsset, onDownloadAsset);
            assetViewer.eventBus.off(ViewerEventType.FocusChange, onFocusChange);
        };
    }

    fallbackBackgroundEventLogging(withPageVisibilityApi = true) {
        if (withPageVisibilityApi && isPageVisibilitySupported() === true) {
            this.pageVisibilityEventLogging();
        }
    }

    pageVisibilityEventLogging() {
        const self = this;

        pageVisibilityChangedListener(() => pageVisibilityChangedHandler());

        function pageVisibilityChangedHandler() {
            if (isPageHidden() === true) {
                self.pause();
            } else if (isPageHidden() === false) {
                self.resume();
            }
        }
    }

    /**
     * When a new AssetViewerView is focussed, most present in the viewport:
     * Reset the last logged event, which emits events in sync and create a new one if not triggered by user
     *
     * @param {AssetViewerItem} assetViewerItem
     * @param {null|Number} page
     * @param {Boolean} fromOrigin, is the event fired from asset-viewer
     */
    handleViewEvents(assetViewerItem, page, fromOrigin = false) {
        let newPage = page;

        // 1-paged document page to 0-based API
        if (page !== null && fromOrigin) {
            newPage = page - 1;
        }

        if (
            hasDeviceEventData(this.lastLoggedEvent) && // if data exists and is valid
            assetViewerItem.id === this.lastLoggedEvent.data.assetViewerItem.id // and if same asset as previously logged
        ) {
            // keep updating, but with new data (whether it's same page or different page)
            this.lastLoggedEvent.data = { assetViewerItem, page: newPage };
            this.updateDeviceEvent(this.lastLoggedEvent);
            return;
        }

        // If the event belongs to a page and another asset is opened on top, we should keep the event running.
        // It should only be stopped by navigating to another asset in the share.
        // The lastLoggedEvent will be restored to this page event once the overlay is closed.
        if (this.childAssetViewer && this.lastLoggedEvent.data && this.lastLoggedEvent.data.assetViewerItem.type === 'page') {
            this.parentPageDeviceEvent = this.lastLoggedEvent;
            this.lastLoggedEvent = {};
        } else if (this.lastLoggedEvent.data && CAN_LOG_UNFOCUSED.indexOf(this.lastLoggedEvent.data.assetViewerItem.type) > -1) {
            // For CAN_LOG_UNFOCUSED assets (videos/audios), don't reset them when scrolling to another asset
            // They should continue logging in the background via lastLoggedEventMap
            // Just clear the lastLoggedEvent reference so a new asset can be tracked
            this.lastLoggedEvent = {};
        } else {
            // Otherwise reset the event.  The case for events that are not page events or page event but where the user navigated to a different asset.
            this.resetLoggedDeviceEvent(this.lastLoggedEvent);
        }

        // Events will be created by user interaction, without timeout and can be ignored,
        if (!this.isDeviceEventHandledBySeparateEvent(assetViewerItem)) {
            const event = this.lastLoggedEvent;
            event.data = { assetViewerItem, page: newPage };
            event.createTimeout = setTimeout(() => {
                this.startDeviceEvent(event, assetViewerItem, newPage, true);
            }, UPDATE_THRESHOLD);
        }
    }

    /**
     * When a new asset is actived for logging by the user (audio, video, web-app, 3d model)
     * If sync use last logged event as it was a view event, if async use the map
     *
     * @param {AssetViewerItem} assetViewerItem
     * @param {null|Number} page
     */
    handleDynamicViewEvent(assetViewerItem, page = null) {
        const hasThreshold = false;

        // Start logging as with a normal view event, except there is no timeout, user manually started the action
        if (CAN_LOG_UNFOCUSED.indexOf(assetViewerItem.type) === INDEX_NOT_FOUND) {
            this.lastLoggedEvent.data = { assetViewerItem, page };
            this.startDeviceEvent(this.lastLoggedEvent, assetViewerItem, page, hasThreshold);
        } else {
            // For CAN_LOG_UNFOCUSED assets (video/audio):
            // 1. If paused briefly (< 2s), resume the existing event
            // 2. If paused too long (>= 2s) or never played, create a new event
            const existingEvent = this.lastLoggedEventMap[assetViewerItem.id];
            const lastPauseTime = this.lastPauseTimeMap[assetViewerItem.id];
            const canResumeEvent = this.canResumePlaybackEvent(existingEvent, lastPauseTime);

            if (canResumeEvent) {
                // Resume the previous event by restarting the heartbeat
                this.resumePlaybackEvent(existingEvent);

                // Clear the pause time since we've resumed
                delete this.lastPauseTimeMap[assetViewerItem.id];

                // Clear the pause timeout since we're resuming before it expires
                if (existingEvent.pauseTimeout) {
                    clearTimeout(existingEvent.pauseTimeout);
                    existingEvent.pauseTimeout = null;
                }

                return;
            }

            // Stop any existing event that can't be resumed (e.g., paused > 2s)
            if (hasDeviceEventData(existingEvent)) {
                this.resetDynamicDeviceEvent(assetViewerItem);
            }

            // Create a new event for this video/audio and store it in the map (allows multiple simultaneous events)
            this.lastLoggedEventMap[assetViewerItem.id] = {};
            this.lastLoggedEventMap[assetViewerItem.id].data = { assetViewerItem, page };

            // If playing from overlay, track this event ID so we can stop it when overlay closes
            if (this.childAssetViewer) {
                this.overlayEventIds.add(assetViewerItem.id);
            }

            this.startDeviceEvent(this.lastLoggedEventMap[assetViewerItem.id], assetViewerItem, page, hasThreshold);
        }
    }

    /**
     * Reset a dynamic device event
     * If sync clear the last logged device event, is async find the data in map and clear
     *
     * @param {AssetViewerItem} assetViewerItem
     */
    resetDynamicDeviceEvent(assetViewerItem) {
        // Stop logging the current last active item in viewport, as normal
        if (CAN_LOG_UNFOCUSED.indexOf(assetViewerItem.type) === INDEX_NOT_FOUND) {
            this.resetLoggedDeviceEvent(this.lastLoggedEvent);
        }
        // Stop logging an item than may be logging while not in viewport, can be found with assetViewerItem.id
        else {
            const loggedEventStore = this.lastLoggedEventMap[assetViewerItem.id];

            // Clear any pending pause timeout to prevent it from firing after reset
            if (loggedEventStore && loggedEventStore.pauseTimeout) {
                clearTimeout(loggedEventStore.pauseTimeout);
                loggedEventStore.pauseTimeout = null;
            }

            // Clear the pause time entry since we're resetting the event
            delete this.lastPauseTimeMap[assetViewerItem.id];

            this.resetLoggedDeviceEvent(loggedEventStore);
        }
    }

    /**
     * Check if we can resume a previous playback event (video/audio)
     * We can resume if:
     * 1. There's an existing event with device event data
     * 2. The pause was brief (< 2 seconds)
     *
     * @param {Object} existingEvent - The existing logged event
     * @param {Number} lastPauseTime - The timestamp when the asset was paused
     * @returns {Boolean}
     */
    canResumePlaybackEvent(existingEvent, lastPauseTime) {
        if (!hasDeviceEventData(existingEvent) || !lastPauseTime) {
            return false;
        }

        const currentTime = new Date().getTime();
        const pauseDuration = currentTime - lastPauseTime;

        return pauseDuration < BRIEF_PAUSE_THRESHOLD;
    }

    /**
     * Resume a paused playback event by restarting the heartbeat updates
     *
     * @param {Object} loggedEventStore - The logged event to resume
     */
    resumePlaybackEvent(loggedEventStore) {
        if (!hasDeviceEventData(loggedEventStore)) {
            return;
        }

        // Restart the heartbeat interval
        loggedEventStore.updateInterval = setInterval(() => {
            if (hasDeviceEventData(loggedEventStore)) {
                logUpdate(loggedEventStore);
                this.eventLogger.stillViewingAsset(loggedEventStore.deviceEvent, loggedEventStore.data.assetViewerItem);
            }
        }, UPDATE_ASSET_THRESHOLD);
    }

    /**
     * Start a new deviceevent, which updates on every UPDATE_ASSET_THRESHOLD interval
     * Stores the data and timeouts in the last logged event or map
     *
     * @param {Object} loggedEventStore
     * @param {Object} asset
     * @param {null|Number} page
     * @param {Boolean} hasThreshold
     */
    startDeviceEvent(loggedEventStore, assetViewerItem, page, hasThreshold = true) {
        const parentPosition = assetViewerItem.parentPosition;
        const parentEventPromise = this.eventLogger
            .startViewingAsset(assetViewerItem, page, parentPosition, hasThreshold)
            .then((deviceEvent) => {
                loggedEventStore.deviceEvent = deviceEvent;
                logStart(loggedEventStore);
                return deviceEvent;
            });
        this.parentEventPromiseMap[assetViewerItem.id] = parentEventPromise;

        parentEventPromise.catch(() => {
            /* Prevent uncaught promise rejection errors when tracking is disabled or the deviceevent call fails */
        });

        loggedEventStore.updateInterval = setInterval(() => {
            if (hasDeviceEventData(loggedEventStore)) {
                logUpdate(loggedEventStore);
                this.eventLogger.stillViewingAsset(loggedEventStore.deviceEvent, loggedEventStore.data.assetViewerItem);
            }
        }, UPDATE_ASSET_THRESHOLD);
    }

    /**
     * Update deviceevent with a new page (same document), same timeout logic as initial create (actually viewing with threshold)
     * Stores the data and timeouts in the last logged event or map
     *
     * @param {Object} loggedEventStore
     */
    updateDeviceEvent(loggedEventStore) {
        // reset possible other queued events
        clearInterval(loggedEventStore.updateInterval);
        clearTimeout(loggedEventStore.createTimeout);

        // update the current event with a new page, and the time it started
        // we also need to pass the same information for the previous page
        loggedEventStore.createTimeout = setTimeout(() => {
            loggedEventStore.deviceEvent.previousPage = loggedEventStore.deviceEvent.page;
            loggedEventStore.deviceEvent.previousPageStartedAt = loggedEventStore.deviceEvent.pageStartedAt;
            loggedEventStore.deviceEvent.page = loggedEventStore.data.page;
            loggedEventStore.deviceEvent.pageStartedAt = new Date().getTime();

            if (hasDeviceEventData(loggedEventStore)) {
                logUpdate(loggedEventStore);
            }

            this.eventLogger.stillViewingAsset(loggedEventStore.deviceEvent, loggedEventStore.data.assetViewerItem);

            loggedEventStore.updateInterval = setInterval(() => {
                if (hasDeviceEventData(loggedEventStore)) {
                    logUpdate(loggedEventStore);

                    // When logging again for the same page (IE: still watching the same page), previousPage becomes the current page
                    loggedEventStore.deviceEvent.previousPage = loggedEventStore.deviceEvent.page;
                    loggedEventStore.deviceEvent.previousPageStartedAt = loggedEventStore.deviceEvent.pageStartedAt;

                    this.eventLogger.stillViewingAsset(loggedEventStore.deviceEvent, loggedEventStore.data.assetViewerItem);
                }
            }, UPDATE_ASSET_THRESHOLD);
        }, UPDATE_THRESHOLD);
    }

    /**
     * Stop a deviceevent, clear interval and deviceevents but keep the asset data
     * Used when browser is unfocussed, on re-focus with the asset data create a new deviceevent if possible
     *
     * @param {Object} loggedEventStore
     */
    stopLoggedDeviceEvent(loggedEventStore) {
        if (!loggedEventStore) {
            return;
        }

        if (loggedEventStore.createTimeout) {
            clearTimeout(loggedEventStore.createTimeout);
            loggedEventStore.createTimeout = null;
        }

        if (loggedEventStore.updateInterval) {
            clearInterval(loggedEventStore.updateInterval);
            loggedEventStore.updateInterval = null;
        }

        if (hasDeviceEventData(loggedEventStore)) {
            logStop(loggedEventStore);

            // When we stop viewing an asset, we need to set the previousPage and previousPageStartedAt to the current page
            // This doesn't need a condition check on the asset type, because it will just be undefined for the other types
            loggedEventStore.deviceEvent.previousPage = loggedEventStore.deviceEvent.page;
            loggedEventStore.deviceEvent.previousPageStartedAt = loggedEventStore.deviceEvent.pageStartedAt;

            this.eventLogger.stopViewingAsset(loggedEventStore.deviceEvent, loggedEventStore.data.assetViewerItem);
            loggedEventStore.deviceEvent = null;
        }
    }

    /**
     * Reset a deviceevent, stops the running events and clear the data
     * Used when asset is no longer logged, no longer in viewport for sync or paused/ended for async dynamic events
     *
     * @param {Object} loggedEventStore
     */
    resetLoggedDeviceEvent(loggedEventStore) {
        this.stopLoggedDeviceEvent(loggedEventStore);
        loggedEventStore.data = null;
        loggedEventStore = null;
    }

    pause() {
        if (this.isLogging === true) {
            this.isLogging = false;

            // Don't stop CAN_LOG_UNFOCUSED assets (videos/audios) when app loses focus
            if (!this.lastLoggedEvent.data || CAN_LOG_UNFOCUSED.indexOf(this.lastLoggedEvent.data.assetViewerItem.type) === -1) {
                this.stopLoggedDeviceEvent(this.lastLoggedEvent);
            }

            this.stopLoggedDeviceEvent(this.parentPageDeviceEvent);

            Object.keys(this.lastLoggedEventMap)
                .map((key) => this.lastLoggedEventMap[key])
                .filter(Boolean)
                .forEach((eventItem) => {
                    // Don't stop CAN_LOG_UNFOCUSED assets (videos/audios) when app loses focus
                    if (eventItem.data && CAN_LOG_UNFOCUSED.indexOf(eventItem.data.assetViewerItem.type) > -1) {
                        return;
                    }

                    this.stopLoggedDeviceEvent(eventItem);
                });

            // Clear pause times when pausing (e.g., when app loses focus)
            this.lastPauseTimeMap = {};
        }
    }

    isDeviceEventHandledBySeparateEvent(assetViewerItem) {
        const isAssetFromPage = typeof assetViewerItem.parentPosition === 'number';
        let assetTypesThatAreHandledBySeparateEvents;

        // Webapp's are handled with the assetViewer event 'ViewerEventType.WebappOpen' when they use the NEW_WINDOW presentationRenderer, if they use the IframePresentationRenderer, they need to follow the regular device event flow
        if (isAssetFromPage) {
            assetTypesThatAreHandledBySeparateEvents = LOG_CREATED_IN_EVENT; // WebApp is rendered inline, follow regular flow
        } else {
            if (this.assetViewer.assetViewerItems.length === 1) {
                assetTypesThatAreHandledBySeparateEvents = LOG_CREATED_IN_EVENT; // WebApp is rendered inline, follow regular flow
            } else {
                assetTypesThatAreHandledBySeparateEvents = [...LOG_CREATED_IN_EVENT, ASSET_TYPE.WEBAPP]; // // WebApp is rendered with NewWindowPresentationRenderer, wait for 'ViewerEventType.WebappOpen' event
            }
        }

        return assetTypesThatAreHandledBySeparateEvents.includes(assetViewerItem.type);
    }

    resume() {
        if (this.isLogging === false && this.canRefocus === true) {
            this.isLogging = true;

            // This resume() function can also be called when the user blurs out from the viewport
            // but comes back to only scroll on top of the document to see other assets, without
            // clicking and not generating a proper focus on the asset viewer. This behavior causes
            // the lastLoggedEvent attribute to get the wrong asset/deviceEvent information.

            // If this is the case, we need to check on what is currently in the viewport and start a device
            // event with that asset rather than the lastLoggedEvent's asset
            const item =
                this.lastLoggedEvent.data && this.lastLoggedEvent.data.assetViewerItem === this.viewportAssetItem.item
                    ? this.lastLoggedEvent.data.assetViewerItem
                    : this.viewportAssetItem.item;

            // Same thing with the page as this is important for the correct continuity logging of the events
            const page =
                this.lastLoggedEvent.data &&
                this.lastLoggedEvent.data.assetViewerItem === this.viewportAssetItem.item &&
                this.lastLoggedEvent.data.page === this.viewportAssetItem.page
                    ? this.lastLoggedEvent.data.page
                    : this.viewportAssetItem.page;

            // Create a new event, start flow again, with last viewed asset, if it has been cleared by pause
            if (this.lastLoggedEvent.data) {
                const assetItem = this.lastLoggedEvent.data.assetViewerItem;
                const isHandledBySeparateEvent = this.isDeviceEventHandledBySeparateEvent(assetItem);
                const assetType = this.lastLoggedEvent.data.assetViewerItem.type;

                // Don't resume CAN_LOG_UNFOCUSED assets (videos/audios) on focus after blur
                // since it should be updating already
                if (CAN_LOG_UNFOCUSED.indexOf(assetType) > -1) {
                    return;
                }

                // For webapp assets, isDeviceEventHandledBySeparateEvent returns false, which would call handleViewEvents.
                // But on resume, we should restart the device event immediately since the user was already viewing it.
                // Check if this is a webapp asset that was actively logging before pause.
                const previouslyLoggingAssetIsWebapp = assetType === ASSET_TYPE.WEBAPP;

                if (!isHandledBySeparateEvent && !previouslyLoggingAssetIsWebapp) {
                    this.handleViewEvents(item, page, true);
                } else {
                    this.startDeviceEvent(this.lastLoggedEvent, this.lastLoggedEvent.data.assetViewerItem, this.lastLoggedEvent.data.page);
                }

                // Resume also gets called when after refreshing and blurring out immediately, we go back and focus an asset.
                // So if this.lastLoggedEvent.data does not exist, it means this edge case happened and we have to build the data
                // with the info from the viewport
            } else {
                this.handleViewEvents(item, page, true);
            }

            if (this.parentPageDeviceEvent) {
                this.startDeviceEvent(
                    this.parentPageDeviceEvent,
                    this.parentPageDeviceEvent.data.assetViewerItem,
                    this.parentPageDeviceEvent.data.page,
                );
            }
        }
    }

    stopRunningDeviceEvents() {
        this.isLogging = false;

        // Keep the page event running.
        if (this.lastLoggedEvent.data && this.lastLoggedEvent.data.assetViewerItem.type !== 'page') {
            this.resetLoggedDeviceEvent(this.lastLoggedEvent);
        }

        // Only stop events from the overlay (childAssetViewer)
        // Main view events (especially CAN_LOG_UNFOCUSED assets like videos/audios) should continue
        Object.keys(this.lastLoggedEventMap)
            .map((key) => this.lastLoggedEventMap[key])
            .filter(Boolean)
            .forEach((eventItem) => {
                // Only stop if this event is from the overlay
                // This method is only triggered by clicking the close button in the asset viewer overlay
                if (eventItem.data && eventItem.data.assetViewerItem && this.overlayEventIds.has(eventItem.data.assetViewerItem.id)) {
                    this.resetLoggedDeviceEvent(eventItem);
                }
            });

        // Clear overlay event IDs after closing overlay
        this.overlayEventIds.clear();
    }

    listenToFocusChange() {
        this.appFocusHelper.subscribe((isAppFocused) => (isAppFocused ? this.resume() : this.pause()));
    }
}
